import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AddStaffModalComponent } from './add-staff-modal.component';
import { ToastService } from '../toast.service';
import { DebounceService } from '../debounce.service';

interface Staff {
    id: string;
    name: string;
    email: string;
    phone_number: string;
    role: string;
    is_active: boolean;
    address: string;
    state: string;
    city: string;
    pincode: string;
    created_at: string;
}

interface Pagination {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
}

@Component({
    selector: 'app-staff',
    standalone: true,
    imports: [CommonModule, FormsModule, SidebarComponent, AddStaffModalComponent],
    templateUrl: './staff.component.html',
    styleUrls: ['./staff.component.scss']
})
export class StaffComponent implements OnInit {
    @ViewChild(AddStaffModalComponent) addModal?: AddStaffModalComponent;

    staff: Staff[] = [];
    pagination: Pagination = {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1,
        hasNext: false,
        hasPrev: false
    };
    searchTerm = '';
    loading = false;
    errorMsg = '';
    showAddModal = false;
    userType = '';

    // Edit
    isEditMode = false;
    editingStaffId: string | null = null;
    selectedStaffData: any = null;

    // Delete confirmation
    showDeleteConfirm = false;
    deletingStaff: Staff | null = null;
    deleting = false;

    constructor(
        private http: HttpClient,
        private toastService: ToastService,
        private debounceService: DebounceService
    ) { }

    ngOnInit() {
        this.loadUserType();
        this.fetchStaff();
    }

    loadUserType() {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                this.userType = user.user_type?.toLowerCase() || '';
            } catch { }
        }
    }

    fetchStaff(page: number = 1) {
        if (this.userType !== 'admin' && this.userType !== 'super_admin') {
            this.loading = false;
            return;
        }

        this.loading = true;
        this.errorMsg = '';

        let url = `${environment.apiBaseUrl}/admin/staff?page=${page}&limit=${this.pagination.limit}`;
        if (this.searchTerm && this.searchTerm.trim().length > 0) {
            url += `&search=${encodeURIComponent(this.searchTerm.trim())}`;
        }

        this.http.get<any>(url).subscribe({
            next: (res) => {
                this.loading = false;
                if (res.statusCode === 200 && res.data) {
                    this.staff = res.data.staff;
                    const pg = res.data.pagination;
                    this.pagination = {
                        ...pg,
                        hasNext: pg.page < pg.totalPages,
                        hasPrev: pg.page > 1
                    };
                } else {
                    this.errorMsg = res.message || 'Failed to fetch staff';
                    this.toastService.error(res.message || 'Failed to fetch staff');
                    this.staff = [];
                }
            },
            error: (err) => {
                this.loading = false;
                this.errorMsg = err?.error?.message || 'Failed to fetch staff';
                this.toastService.error(err?.error?.message || 'Failed to fetch staff');
                this.staff = [];
            }
        });
    }

    onSearch(term: string) {
        this.searchTerm = term;
        this.debounceService.debounce('staff-search', () => this.fetchStaff(1));
    }

    goToPage(page: number) {
        if (page < 1 || page > this.pagination.totalPages) return;
        this.fetchStaff(page);
    }

    // --- Add Modal ---
    openAddModal() {
        if (this.userType === 'admin' || this.userType === 'super_admin') {
            this.isEditMode = false;
            this.editingStaffId = null;
            this.selectedStaffData = null;
            this.showAddModal = true;
        }
    }

    closeAddModal() {
        this.showAddModal = false;
        this.isEditMode = false;
        this.editingStaffId = null;
        this.selectedStaffData = null;
    }

    handleAddStaff(payload: any) {
        if (this.userType !== 'admin' && this.userType !== 'super_admin') return;

        if (this.isEditMode && this.editingStaffId) {
            this.http.patch<any>(`${environment.apiBaseUrl}/admin/update-staff/${this.editingStaffId}`, payload).subscribe({
                next: (res) => {
                    if (res.statusCode === 200) {
                        this.toastService.success('Staff member updated successfully!');
                        this.closeAddModal();
                        this.fetchStaff(this.pagination.page);
                    } else {
                        this.toastService.error(res.message || 'Failed to update staff');
                        this.addModal?.resetSubmitting();
                    }
                },
                error: (err) => {
                    this.toastService.error(err?.error?.message || 'Failed to update staff');
                    this.addModal?.resetSubmitting();
                }
            });
        } else {
            this.http.post<any>(`${environment.apiBaseUrl}/admin/create-staff`, payload).subscribe({
                next: (res) => {
                    if (res.statusCode === 200 || res.statusCode === 201) {
                        this.toastService.success(res.message || 'Staff member added successfully!');
                        this.closeAddModal();
                        this.fetchStaff(1);
                    } else {
                        this.toastService.error(res.message || 'Failed to add staff');
                        this.addModal?.resetSubmitting();
                    }
                },
                error: (err) => {
                    this.toastService.error(err?.error?.message || 'Failed to add staff');
                    this.addModal?.resetSubmitting();
                }
            });
        }
    }

    // --- Edit ---
    editStaff(s: Staff) {
        this.isEditMode = true;
        this.editingStaffId = s.id;
        this.selectedStaffData = { ...s };
        this.showAddModal = true;
    }

    // --- Delete ---
    deleteStaff(s: Staff) {
        this.deletingStaff = s;
        this.showDeleteConfirm = true;
    }

    cancelDelete() {
        this.showDeleteConfirm = false;
        this.deletingStaff = null;
        this.deleting = false;
    }

    confirmDelete() {
        if (!this.deletingStaff) return;
        this.deleting = true;

        this.http.delete<any>(`${environment.apiBaseUrl}/admin/delete-staff/${this.deletingStaff.id}`).subscribe({
            next: (res) => {
                if (res.statusCode === 200) {
                    this.toastService.success('Staff member deleted successfully!');
                    this.cancelDelete();
                    this.fetchStaff(this.pagination.page);
                } else {
                    this.toastService.error(res.message || 'Failed to delete staff');
                    this.deleting = false;
                }
            },
            error: (err) => {
                this.toastService.error(err?.error?.message || 'Failed to delete staff');
                this.deleting = false;
            }
        });
    }

    getStatusClass(isActive: boolean) {
        return isActive ? 'active' : 'inactive';
    }
}
